// Single state tree + useReducer. No state library for the MVP.
// Multi-project: projects[] + currentId, persisted to local localStorage.
// Multi-page: project.pages[] + currentPageId; storage v3, with automatic migration from v2 (single canvas)/v1.

import type { CanvasOp, CanvasObject, CanvasState, ChatMsg, ChatProcessStep, ComponentDef, JobRecord, Page } from './types';
import type { CritiqueIssue } from './api';
import { collectDescendants, hasLayout, resolveLayout } from './lib/layout';
import { applyOpLocally } from './lib/canvasOps';

// SPEC-464 M1.4: web 渲染侧布局选项 — iconSnap 恒开（web 侧无 env 通道，勿发明机制；
// 渲染与 worker doc SoT / critique facts 三方一致是红线 — spec M1.4）。worker 侧
// ICON_OPTICAL=off 回退的是 worker 面（校验/snap/仲裁文案）；渲染面回退 = 部署回滚
//（SPEC-462 M3 先例）。函数级缺省 off 保证旧调用方（未传 options）byte-identical。
const WEB_LAYOUT_OPTS = { iconSnap: true };
import { buildDoneText, isRepeatClaim, reconcileFixClaims } from './lib/doneText';

/**
 * Parse the worker's generation size tier (e.g. '1664*928') into an aspect ratio h/w.
 * Returns null when the format is invalid or missing (e.g. edit jobs carry no size).
 */
function parseGenSizeRatio(size?: string): number | null {
  if (!size) return null;
  const parts = size.split('*');
  const w = Number(parts[0]), h = Number(parts[1]);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return h / w;
}

/**
 * Parse the worker's generation size tier (e.g. '1664*928') into natural pixel dimensions.
 * SPEC-464 M2.3: 生成档尺寸即源图 natural size（provider 输出 = 档尺寸），JOB_UPDATE 落地时
 * pin 进带 fit 的 image 对象（src_w/src_h = 源比例 SoT — worker facts/审计/forensics 同源）。
 * Returns null when the format is invalid or missing (edit jobs carry no size — 不伪造).
 */
function parseGenSizeWH(size?: string): { w: number; h: number } | null {
  if (!size) return null;
  const parts = size.split('*');
  const w = Number(parts[0]), h = Number(parts[1]);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return { w, h };
}

export interface Project {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  pages: Page[];
  chat: ChatMsg[];
  /** Gallery thumbnail (JPEG data URL, long edge ≤420px, dropped above ~150KB → undefined). SPEC-404 */
  thumb?: string;
}

/** Top-level view: home = project gallery, workbench = canvas editor. Persisted; boot always lands on home (SPEC-404). */
export type View = 'home' | 'workbench';

/** SPEC-454 M3: one deleted-project record; cloud docs carry them in a top-level `deleted` array so
 * the server-side union merge can never resurrect a deletion (LWW against the project's updatedAt). */
export interface Tombstone { id: string; deletedAt: number }

export interface AppState {
  projects: Project[];
  currentId: string | null;
  currentPageId: string | null;
  /** Home gallery vs canvas workbench (SPEC-404) */
  view: View;
  selectedId: string | null;
  /** Multi-select: the authoritative selection set; selectedId = the last element (keeps single-select panels/Transformer working) */
  selectedIds: string[];
  /** Component library: global (stored at the doc top level, belongs to no project), reusable across projects */
  components: ComponentDef[];
  /** SPEC-454 M3: tombstones of deleted projects — persisted at the doc top level (anti-resurrection
   * under the server-side union merge). Pre-M3 docs have no field → parsed as []. */
  deleted: Tombstone[];
  jobs: Record<string, JobRecord>;
  busy: boolean; // agent is thinking
  /** undo/redo: snapshot stacks (never persisted; buildPersistedDoc picks only the business fields).
   * Entries carry the project they affect (scope); undo/redo in one project never rewrites another (SPEC-371). */
  past: HistEntry[];
  future: HistEntry[];
}

/** undo/redo snapshot = the document-content fields (excludes runtime state like selection/jobs/busy).
 * SPEC-454 B4: `deleted` rides along so a doc-level undo also rolls the tombstone array back. */
export interface DocSnap {
  projects: Project[];
  currentId: string | null;
  currentPageId: string | null;
  components: ComponentDef[];
  deleted: Tombstone[];
}

/** One undo stack entry: the pre-action snapshot + which project it affects.
 * scope = projectId for project-local actions; null for doc-level actions (project list / component library),
 * whose undo legitimately restores the whole document including currentId. */
export interface HistEntry {
  snap: DocSnap;
  scope: string | null;
}

export const initialState: AppState = {
  projects: [],
  currentId: null,
  currentPageId: null,
  view: 'home',
  selectedId: null,
  selectedIds: [],
  components: [],
  deleted: [],
  jobs: {},
  busy: false,
  past: [],
  future: [],
};

/** Short unique id (objects/components/groups). Collision probability within one store is negligible; not a security context. */
export function uid(prefix: string): string {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

export function currentProject(state: AppState): Project | null {
  return state.projects.find(p => p.id === state.currentId) ?? null;
}

/** Current page; falls back to the first page when currentPageId is stale (page deleted / old data). */
export function currentPage(state: AppState): Page | null {
  const p = currentProject(state);
  if (!p || p.pages.length === 0) return null;
  return p.pages.find(pg => pg.id === state.currentPageId) ?? p.pages[0];
}

export function freshCanvas(w = 1080, h = 1440): CanvasState {
  return { artboard: { w, h, bg: '#ffffff' }, objects: [] };
}

function freshPage(artboard?: { w: number; h: number; bg?: string }, name = '1'): Page {
  return {
    id: `pg${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    name,
    canvas: {
      artboard: { w: artboard?.w ?? 1080, h: artboard?.h ?? 1440, bg: artboard?.bg ?? '#ffffff' },
      objects: [],
    },
  };
}

export function makeProject(name: string, artboard?: { w: number; h: number }): Project {
  const now = Date.now();
  return {
    id: `p${now.toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    name,
    createdAt: now,
    updatedAt: now,
    pages: [freshPage(artboard)],
    chat: [],
  };
}

export type Action =
  | { type: 'USER_MSG'; text: string; images?: string[] }
  | { type: 'AGENT_START' }
  | { type: 'AGENT_RESULT'; projectId: string; pageId: string; reply: string; ops: CanvasOp[]; jobs: { id: string; kind: 'gen' | 'edit'; status: string }[]; error?: string; retryText?: string; projectTitle?: string; plan?: { key?: string; digest: string }; goal?: { met: boolean; coverage: number; missing: string[] }; noReference?: boolean }
  | { type: 'AGENT_ERROR'; error: string; retryText?: string; projectId?: string }
  /** SPEC-392 段B: stop-button cancel while a refine round is mid-flight — just release the busy flag (no bubble). */
  | { type: 'AGENT_CANCEL' }
  /** SPEC-392 段B: append an assistant bubble without touching canvas/busy — the review loop's done message (carries the process log).
   * SPEC-426 M2 A.3/A.4 (修复轮): optionally carries the budget-exhausted residual list + dimension
   * scores; a fresh done bubble without them CLEARS stale values on merge (继续修正用后清空防重复触发). */
  | { type: 'CHAT_APPEND'; projectId: string; text: string; process?: ChatProcessStep[]; residual?: CritiqueIssue[]; scores?: Record<string, number>; frozen?: boolean }
  /** SPEC-463 M2.2（E2 根治）: 终局补审 fail 且 done 泡已披露 exhausted — 合并改写最近一条
   * assistant 泡为单一终局陈述（exhausted 文案至多 ×1；text 由 App 以 mergeReauditReport 按
   * 合并去重后计数组装，process 拼接补审相位，residual/scores 由合并后清单拥有）。
   * 无可改写泡 → 按 CHAT_APPEND 追加语义落新泡（诚实披露绝不丢）。 */
  | { type: 'REAUDIT_MERGE'; projectId: string; text: string; process?: ChatProcessStep[]; residual?: CritiqueIssue[]; scores?: Record<string, number> }
  /** SPEC-427 B: 宣称对账 — App 审查循环在下一轮 critique 对账命中（verdict 指纹零变化 + ops diff 为空）
   * 时发出：最近一条 assistant 气泡的「已修复」类宣称句改写为「修正未生效（…问题仍在：<清单>）」。 */
  | { type: 'RECONCILE_CLAIM'; projectId: string; issues: CritiqueIssue[] }
  | { type: 'JOB_UPDATE'; job: JobRecord }
  | { type: 'SELECT'; id: string | null; additive?: boolean }
  | { type: 'SELECT_MANY'; ids: string[]; additive?: boolean }
  | { type: 'GROUP' }
  | { type: 'UNGROUP' }
  // Multi-select alignment: relative to the bounding box of the selected objects
  | { type: 'ALIGN_MANY'; dir: 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom' }
  /** SPEC-426 M2 提示2: optional explicit batch target set (`ids`) — moved by the same delta as
   * `id`; falls back to the current selection when omitted (pre-M2 behavior byte-identical). */
  | { type: 'MOVE_MANY'; id: string; x: number; y: number; ids?: string[] }
  | { type: 'REMOVE_MANY'; ids: string[] }
  /** SPEC-426 M2 提示2: `id` accepts a batch array — one patch, many targets, one undo entry
   * (multi-select prop edits). Single-string dispatches behave exactly as before. */
  | { type: 'PATCH_OBJECT'; id: string | string[]; patch: Partial<CanvasObject>; projectId?: string; pageId?: string }
  /** SPEC-426 M2 C.2 (修复轮): layer-panel double-click rename — object-level counterpart to
   * PROJECT_RENAME/PAGE_RENAME (trim, empty ignored, undoable, same-name no-op). */
  | { type: 'RENAME_OBJECT'; id: string; name: string }
  /** SPEC-415 T-703: swap an object's src to the relayed WebP copy. Race guard in reducer: object must still exist globally with src===oldSrc.
   * Deliberately NOT UNDOABLE — the old key becomes a GC orphan right after the swap; undo would restore a dead URL. */
  | { type: 'IMG_RELAYED'; id: string; oldSrc: string; newSrc: string }
  | { type: 'TOGGLE_VISIBLE'; id: string }
  | { type: 'REORDER'; id: string; dir: 'up' | 'down' }
  /** Layer drag-reorder — insert directly at the target objects-array index (0 = bottom-most). toIndex refers to the array after removal. */
  | { type: 'REORDER_TO'; id: string; toIndex: number }
  | { type: 'REMOVE'; id: string }
  | { type: 'PROJECT_NEW'; name: string; artboard?: { w: number; h: number } }
  | { type: 'CANVAS_RESIZE'; w: number; h: number }
  | { type: 'CANVAS_BG'; color: string }
  | { type: 'PROJECT_SWITCH'; id: string }
  | { type: 'PROJECT_DELETE'; id: string }
  | { type: 'PROJECT_RENAME'; id: string; name: string }
  | { type: 'PROJECT_THUMB'; id: string; thumb: string | undefined }
  | { type: 'SET_VIEW'; view: View }
  | { type: 'PAGE_NEW'; name: string }
  | { type: 'PAGE_SWITCH'; id: string }
  | { type: 'PAGE_RENAME'; id: string; name: string }
  | { type: 'PAGE_DELETE'; id: string }
  // Component library
  | { type: 'COMPONENT_SAVE'; name: string }
  | { type: 'COMPONENT_DELETE'; id: string }
  | { type: 'COMPONENT_INSERT'; id: string }
  | { type: 'LOAD'; state: AppState }
  // undo/redo
  | { type: 'UNDO' }
  | { type: 'REDO' };

/** Apply one change to the current project's pages/chat and bump updatedAt. Returns state unchanged when there is no current project. */
function withCurrent(state: AppState, fn: (p: Project) => Pick<Partial<Project>, 'pages' | 'chat'>): AppState {
  if (!state.currentId) return state;
  return {
    ...state,
    projects: state.projects.map(p =>
      p.id === state.currentId ? { ...p, ...fn(p), updatedAt: Date.now() } : p
    ),
  };
}

/** Apply one change to a specific project (so async results land back on the sender). Returns state unchanged when the project doesn't exist. */
function withProject(state: AppState, projectId: string, fn: (p: Project) => Pick<Partial<Project>, 'pages' | 'chat' | 'name'>): AppState {
  if (!state.projects.some(p => p.id === projectId)) return state;
  return {
    ...state,
    projects: state.projects.map(p =>
      p.id === projectId ? { ...p, ...fn(p), updatedAt: Date.now() } : p
    ),
  };
}

/** Apply a change to a specific page of a specific project. Returns state unchanged when the page doesn't exist (page deleted after dispatch → ops dropped). */
function withProjectPage(state: AppState, projectId: string, pageId: string, fn: (page: Page) => Page): AppState {
  return withProject(state, projectId, p => {
    const page = p.pages.find(pg => pg.id === pageId);
    if (!page) return {};
    return { pages: p.pages.map(pg => pg.id === pageId ? fn(pg) : pg) };
  });
}

/** Apply a change to the current project's current page. Returns state unchanged when the page doesn't exist. */
function withCurrentPage(state: AppState, fn: (page: Page) => Page): AppState {
  return withCurrent(state, p => {
    const page = p.pages.find(pg => pg.id === state.currentPageId) ?? p.pages[0];
    if (!page) return {};
    const next = fn(page);
    return { pages: p.pages.map(pg => pg.id === page.id ? next : pg) };
  });
}

export function reducer(state: AppState, action: Action): AppState {
  // ===== undo/redo wrapper layer (project-scoped, SPEC-371) =====
  // Undo in project B applies the nearest entry belonging to B (or a doc-level entry) — it skips entries owned by
  // other projects instead of rolling them back, so undo can no longer rewrite another project's pages or teleport
  // the user to it. Trade-off: cross-project interleaving (edit A, switch B, undo with no B history) walks past A's
  // entry; that is deliberate — per-project semantics beat global chronology here.
  if (action.type === 'UNDO') {
    if (state.past.length === 0) return state;
    let i = state.past.length - 1;
    while (i >= 0) {
      const s = state.past[i].scope;
      if (s === null || s === state.currentId) break;
      i--;
    }
    if (i < 0) return state;
    const entry = state.past[i];
    const next = entry.scope === null
      ? applySnap(state, entry.snap)
      : restoreProjectSubtree(state, entry.snap, entry.scope);
    const past = [...state.past.slice(0, i), ...state.past.slice(i + 1)];
    return { ...next, past, future: [{ snap: takeSnap(state), scope: entry.scope }, ...state.future].slice(0, HISTORY_CAP) };
  }
  if (action.type === 'REDO') {
    if (state.future.length === 0) return state;
    let i = 0;
    while (i < state.future.length) {
      const s = state.future[i].scope;
      if (s === null || s === state.currentId) break;
      i++;
    }
    if (i >= state.future.length) return state;
    const entry = state.future[i];
    const next = entry.scope === null
      ? applySnap(state, entry.snap)
      : restoreProjectSubtree(state, entry.snap, entry.scope);
    const future = [...state.future.slice(0, i), ...state.future.slice(i + 1)];
    return { ...next, past: [...state.past, { snap: takeSnap(state), scope: entry.scope }].slice(-HISTORY_CAP), future };
  }
  const next = baseReducer(state, action);
  if (next === state) return state;
  if (action.type === 'LOAD') return { ...next, past: [], future: [] };
  if (UNDOABLE.has(action.type)) {
    // Push onto the stack only when the document actually changed: JOB_UPDATE polling (queued/running) doesn't touch objects;
    // if every 2s tick pushed a snapshot, the 50-entry cap would fill instantly and future would be cleared repeatedly
    if (next.projects === state.projects && next.components === state.components) return next;
    return { ...next, past: [...state.past, { snap: takeSnap(state), scope: scopeOf(state, action) }].slice(-HISTORY_CAP), future: [] };
  }
  return next;
}

/** Actions that change the project list or the global component library — their undo restores the whole document
 * (scope=null); the currentId jump in that case is the sanctioned exception (undoing "delete project X" returns to X). */
const DOC_LEVEL = new Set<Action['type']>(['PROJECT_NEW', 'PROJECT_DELETE', 'COMPONENT_SAVE', 'COMPONENT_DELETE']);

/** Which project an UNDOABLE action affects (snapshot attribution at push time). */
function scopeOf(state: AppState, action: Action): string | null {
  if (DOC_LEVEL.has(action.type)) return null;
  if (action.type === 'PROJECT_RENAME') return action.id;
  if (action.type === 'AGENT_RESULT') return action.projectId;
  if (action.type === 'PATCH_OBJECT' && action.projectId) return action.projectId;
  if (action.type === 'JOB_UPDATE') {
    // Job results patch by job_id wherever the object lives — attribute to that project, not to the viewer's current one
    for (const p of state.projects) {
      if (p.pages.some(pg => pg.canvas.objects.some(o => o.job_id === action.job.id))) return p.id;
    }
    return state.currentId;
  }
  return state.currentId;
}

/** Actions that change document content and deserve a place on the undo stack. Selection/chat input/job status are not undoable. */
const UNDOABLE = new Set<Action['type']>([
  'AGENT_RESULT', 'JOB_UPDATE',
  'GROUP', 'UNGROUP', 'ALIGN_MANY', 'MOVE_MANY', 'REMOVE_MANY', 'PATCH_OBJECT', 'RENAME_OBJECT', 'TOGGLE_VISIBLE',
  'REORDER', 'REORDER_TO', 'REMOVE',
  'PROJECT_NEW', 'PROJECT_DELETE', 'PROJECT_RENAME',
  'CANVAS_RESIZE', 'CANVAS_BG',
  'PAGE_NEW', 'PAGE_RENAME', 'PAGE_DELETE',
  'COMPONENT_SAVE', 'COMPONENT_DELETE', 'COMPONENT_INSERT',
]);
const HISTORY_CAP = 50;

function takeSnap(state: AppState): DocSnap {
  return { projects: state.projects, currentId: state.currentId, currentPageId: state.currentPageId, components: state.components, deleted: state.deleted };
}

/** Union tombstones by id keeping the newest deletedAt (SPEC-454 M3). */
function dedupeTombstones(list: Tombstone[]): Tombstone[] {
  const byId = new Map<string, Tombstone>();
  for (const t of list) {
    const cur = byId.get(t.id);
    if (!cur || t.deletedAt >= cur.deletedAt) byId.set(t.id, t);
  }
  return [...byId.values()];
}

/**
 * Restore a snapshot: document content rolls back, but **each project's current chat log is preserved** (undo is a
 * canvas operation — it must not undo conversation bubbles too); projects created after the snapshot naturally
 * disappear (correct semantics). The selection is cleared (undone objects may no longer exist); jobs/busy untouched.
 */
function applySnap(state: AppState, snap: DocSnap): AppState {
  const chatById = new Map(state.projects.map(p => [p.id, p.chat] as const));
  const now = Date.now();
  const stateIds = new Set(state.projects.map(p => p.id));
  // SPEC-454 B4 (undo × tombstone): a project RE-APPEARING through a doc-level undo/redo (undo of a
  // delete) re-enters via the rebuild channel — updatedAt must end up strictly greater than the
  // tombstone's deletedAt (same-millisecond delete→undo included), so the server's LWW branch keeps
  // the project and drops the tombstone.
  const tombById = new Map(state.deleted.map(t => [t.id, t.deletedAt]));
  const projects = snap.projects.map(p => {
    const kept = { ...p, chat: chatById.get(p.id) ?? p.chat };
    if (stateIds.has(p.id)) return kept;
    const dAt = tombById.get(p.id);
    return { ...kept, updatedAt: Math.max(now, dAt !== undefined ? dAt + 1 : 0) };
  });
  // SPEC-454 B4: a project DISAPPEARING through a doc-level undo/redo (undo of a create) must leave a
  // tombstone, or the server-side union would resurrect it from the stored doc. The client has no
  // per-project "was pushed" marker → superset: tombstone every disappearing id (harmless for ids
  // never pushed — the server's union never contained them).
  const snapIds = new Set(snap.projects.map(p => p.id));
  const deleted = dedupeTombstones([
    ...snap.deleted,
    ...state.projects.filter(p => !snapIds.has(p.id)).map(p => ({ id: p.id, deletedAt: now })),
  ]);
  return {
    ...state,
    projects,
    currentId: snap.currentId,
    currentPageId: snap.currentPageId,
    components: snap.components,
    deleted,
    selectedId: null,
    selectedIds: [],
  };
}

/** Restore only one project's subtree (name + pages) from a snapshot — undo in the current project never touches the
 * other projects and never teleports currentId/currentPageId (SPEC-371). Chat is preserved, same as applySnap. */
function restoreProjectSubtree(state: AppState, snap: DocSnap, projectId: string): AppState {
  const sp = snap.projects.find(p => p.id === projectId);
  if (!sp) return state; // project absent in snapshot — cannot happen for project-scoped entries
  return {
    ...state,
    projects: state.projects.map(p => (p.id === projectId ? { ...p, name: sp.name, pages: sp.pages } : p)),
    selectedId: null,
    selectedIds: [],
  };
}

/** Generated default project names in both locales (project.untitled + counter). Only these are eligible for
 * auto-naming (SPEC-375) — a name the user typed themselves is never overwritten. */
const AUTO_NAME_RE = /^(未命名设计|Untitled design) \d+$/;

/** True when the project still carries its generated default name — the gate for requesting an auto name (SPEC-377). */
export function isDefaultProjectName(name: string): boolean {
  return AUTO_NAME_RE.test(name);
}

function baseReducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'USER_MSG':
      return withCurrent(state, p => ({ chat: [...p.chat, { role: 'user' as const, text: action.text, ts: Date.now(), ...(action.images?.length ? { images: action.images } : {}) }] }));
    case 'AGENT_START':
      return { ...state, busy: true };
    case 'AGENT_CANCEL':
      return { ...state, busy: false };
    case 'AGENT_RESULT': {
      const failed = !!action.error;
      // Async results must land back on **the project/page that initiated the request**. The old withCurrentPage
      // landed on whatever project was current when the result arrived — if the user deleted/switched the initiating
      // project mid-flight, ops leaked into whichever project happened to be open (objects left in the wrong project).
      // Initiating project deleted → ops+chat are dropped entirely.
      let next: AppState = state;
      if (state.projects.some(p => p.id === action.projectId)) {
        if (!action.error) {
          next = withProjectPage(next, action.projectId, action.pageId, pg => ({ ...pg, canvas: applyOps(pg.canvas, action.ops) }));
        }
        // Auto project naming (SPEC-375): the planner proposes a name on the first turn; it only replaces the
        // generated default name. User-renamed projects are never touched. Missing/garbled title = silent skip.
        if (action.projectTitle) {
          const title = action.projectTitle;
          next = withProject(next, action.projectId, p => (AUTO_NAME_RE.test(p.name) ? { name: title } : {}));
        }
        // SPEC-426 M2 A.1 (修复轮): every worker reply landing in chat passes buildDoneText — the
        // single choke point covering ALL AGENT_RESULT dispatch sites (main send / refine rounds /
        // reattach / region edit). These bubbles never carry critique evidence (review phases ride
        // the loop's CHAT_APPEND done bubble), so unbacked 自检-style claims are stripped here.
        // SPEC-427 D: 发起方（App）已判定 insistsNoReference 路径时携 noReference — 无 passing
        // critique 证据的完整性宣称（完整搭建/可交付等）与期货宣称在此剥离（取证 msg[5]）。
        const finalPg = next.projects.find(pr => pr.id === action.projectId)?.pages.find(pg => pg.id === action.pageId);
        // SPEC-453 M3-3: action.goal（met=false）→ doneText 诚实收口层（剥完成态断言+带未达项摘要）。
        const replyText = buildDoneText(finalPg?.canvas ?? null, action.reply, undefined,
          action.noReference || action.goal
            ? { ...(action.noReference ? { noReference: true } : {}), ...(action.goal ? { goal: action.goal } : {}) }
            : undefined);
        next = withProject(next, action.projectId, p => {
          // SPEC-427 A 同文案复读抑制：连续 assistant 落泡且归一化后包含关系 → 合并为一条，
          // 不重复推送（取证：同一「两项精准修正」宣称连推 7 次）。错误泡/plan 泡不参与合并。
          const lastMsg = p.chat[p.chat.length - 1];
          if (!failed && !action.plan && lastMsg && lastMsg.role === 'assistant' && !lastMsg.error &&
              isRepeatClaim(lastMsg.text, replyText)) {
            return p;
          }
          return {
            chat: [...p.chat, {
              role: 'assistant' as const, text: replyText, ts: Date.now(),
              ...(failed ? { error: true, retryText: action.retryText } : {}),
              // SPEC-413 M1b (§6 决策 a): the plan card rides the done bubble as a collapsed process step.
              ...(action.plan ? { process: [{ phase: 'plan' as const, ts: Date.now(), planDigest: action.plan.digest, ...(action.plan.key ? { planKey: action.plan.key } : {}) }] } : {}),
            }],
          };
        });
      }
      const jobs = { ...next.jobs };
      for (const j of action.jobs) {
        jobs[j.id] = { id: j.id, kind: j.kind, status: 'queued', attempts: 0 };
      }
      return { ...next, busy: false, jobs };
    }
    case 'AGENT_ERROR': {
      // With a projectId, the error bubble also lands on the sender; without one (e.g. job retry) it goes to the current project.
      const appendError = (p: Project) => ({
        chat: [...p.chat, {
          role: 'assistant' as const, text: action.error, ts: Date.now(),
          error: true, retryText: action.retryText,
        }],
      });
      const next = action.projectId
        ? withProject(state, action.projectId, appendError)
        : withCurrent(state, appendError);
      return { ...next, busy: false };
    }
    case 'CHAT_APPEND': {
      // Review-loop done bubble (SPEC-392 段B): chat-only, scoped to the originating project;
      // not UNDOABLE (chat bubbles never ride the undo stack — same as AGENT_RESULT's chat half).
      // SPEC-426 M2 提示2: consecutive assistant appends in the same project merge into ONE
      // bubble — the done text rides after the draft reply and process steps concatenate (plan
      // card + review phases keep the full audit trail on one card). Error bubbles (retry
      // affordance) and user bubbles are never merged into.
      return withProject(state, action.projectId, p => {
        const last = p.chat[p.chat.length - 1];
        if (last && last.role === 'assistant' && !last.error) {
          const proc = [...(last.process ?? []), ...(action.process ?? [])];
          const merged: ChatMsg = {
            ...last,
            text: last.text ? `${last.text}\n\n${action.text}` : action.text,
            ts: Date.now(),
            ...(proc.length > 0 ? { process: proc } : {}),
            // SPEC-426 M2 A.3/A.4 (修复轮): the fresh done bubble OWNS residual/scores — absent
            // values clear stale ones (undefined keys drop out of JSON persistence = 零迁移).
            residual: action.residual,
            scores: action.scores,
            // SPEC-427 A: frozen 位同样由新 done 泡拥有（缺省清除旧值）。
            frozen: action.frozen,
          };
          return { chat: [...p.chat.slice(0, -1), merged] };
        }
        return {
          chat: [...p.chat, {
            role: 'assistant' as const, text: action.text, ts: Date.now(),
            ...(action.process?.length ? { process: action.process } : {}),
            ...(action.residual ? { residual: action.residual } : {}),
            ...(action.scores ? { scores: action.scores } : {}),
            ...(action.frozen ? { frozen: true } : {}),
          }],
        };
      });
    }
    case 'REAUDIT_MERGE': {
      // SPEC-463 M2.2: 只改写最近一条 assistant 泡（终局陈述单一化：exhausted 至多 ×1）。
      // process 拼接（全审计链保留，CHAT_APPEND 合并语义同款）；residual/scores 由本次合并
      // 结果拥有；frozen 位随 ...last 保留。user/error 泡不碰 — 防御性退化为追加新泡
      //（竞态下诚实披露绝不丢；不可 undo，chat 泡不进 undo 栈 — CHAT_APPEND 同纪律）。
      return withProject(state, action.projectId, p => {
        const last = p.chat[p.chat.length - 1];
        if (last && last.role === 'assistant' && !last.error) {
          const proc = [...(last.process ?? []), ...(action.process ?? [])];
          const merged: ChatMsg = {
            ...last,
            text: action.text,
            ts: Date.now(),
            ...(proc.length > 0 ? { process: proc } : {}),
            residual: action.residual,
            scores: action.scores ?? last.scores,
          };
          return { chat: [...p.chat.slice(0, -1), merged] };
        }
        return {
          chat: [...p.chat, {
            role: 'assistant' as const, text: action.text, ts: Date.now(),
            ...(action.process?.length ? { process: action.process } : {}),
            ...(action.residual ? { residual: action.residual } : {}),
            ...(action.scores ? { scores: action.scores } : {}),
          }],
        };
      });
    }
    case 'RECONCILE_CLAIM': {
      // SPEC-427 B: 只改写最近一条 assistant 气泡（refine 轮宣称落泡点）；user/error 泡不碰，
      // 无宣称句时字节不变 → 原状态引用透传（不触发重渲染）。
      const proj = state.projects.find(p => p.id === action.projectId);
      const last = proj?.chat[proj.chat.length - 1];
      if (!proj || !last || last.role !== 'assistant' || last.error) return state;
      const rewritten = reconcileFixClaims(last.text, { verdictChanged: false, opsEmpty: true, remaining: action.issues });
      if (rewritten === last.text) return state;
      return withProject(state, action.projectId, p => ({
        chat: [...p.chat.slice(0, -1), { ...last, text: rewritten, ts: Date.now() }],
      }));
    }
    case 'JOB_UPDATE': {
      const jobs = { ...state.jobs, [action.job.id]: action.job };
      // Which project/page a job belongs to is not certain (the user may have switched) — search globally by job_id for the page holding it
      const patchObjects = (objects: CanvasObject[]): CanvasObject[] | null => {
        if (!objects.some(o => o.job_id === action.job.id)) return null;
        if (action.job.status === 'done' && action.job.result_image) {
          // Aspect-ratio correction: add_image's w/h comes from the planning model arbitrarily,
          // but generation snaps to 5 size tiers (worker GEN_SIZE_TABLE); when w/h disagree with the real image,
          // Konva stretches it (squashed or erratic scaling). When the job completes, correct via job.size:
          // keep width fixed, adjust height to the generated ratio.
          // SPEC-457 M2 (IMAGE_SLOT): 槽位图（有 parent）修正后高度上限 = 槽高 — 生成档已按槽比例
          // 选档（worker pickGenSize），此处为兜底卡尺，aspect 修正永不撑爆槽（overflow_parent 源头之一）；
          // 无 parent 的图维持现行为（不设上限）；槽 frame 不存在/非法高时优雅回退现行为。
          const ratio = parseGenSizeRatio(action.job.size);
          // SPEC-464 M2: 源尺寸 pin 数据 — 生成档尺寸即源图 natural size，直接解析无需加载图片。
          const genWH = parseGenSizeWH(action.job.size);
          return objects.map(o => {
            if (o.job_id !== action.job.id) return o;
            const done = { ...o, src: action.job.result_image!, pending: false, error: null };
            // SPEC-464 M2.3: 落地 pin src_w/src_h（源比例 SoT）。对象带 fit（worker IMAGE_FIT
            // on 面发射的证据 — web 无 env 通道，一律按字段有无分支）且生成档可解析 → pin；
            // fit 缺席（off 面/存量对象）→ 零新键 byte-identical；edit job 无 size → 不 pin。
            const landed = o.fit !== undefined && genWH ? { ...done, src_w: genWH.w, src_h: genWH.h } : done;
            if (ratio == null || !o.w) return landed;
            // SPEC-462 M2: absolute 脱流子（背景槽）= border-box 全覆盖语义 — aspect 修正跳过，
            // w/h 保持父 border-box 尺寸（生成档已按槽比例 pickGenSize 选档，stretch-to-cover
            // 正是背景图意图）；流内子槽维持现行槽高卡尺。
            // SPEC-464 M2.3: absolute 早退保留（整幅背景 = cover 满铺语义）— pin 照旧生效。
            if (o.position === 'absolute') return landed;
            // SPEC-464 M2.3: 卡尺尊重 fit — cover/contain → 落地盒 = 槽真盒（比例差由渲染面
            // 裁剪/留白吸收，不再动盒几何拉伸）；fill/缺席 → 现行 aspect 修正 + 槽高卡尺
            //（off 路径 byte-identical）。
            if (o.fit === 'cover' || o.fit === 'contain') return landed;
            let h = Math.round(o.w * ratio);
            // SPEC-463 M1.2（E1 根治）: 卡尺以槽为准 — 对象上 pin 的 slot_h（占位替换保全的
            // 真槽高：商品卡槽 100，非整卡 frame 210）优先；无 slot_h 才回退 parent frame 高
            //（SPEC-457 M2 现行为）；无 slot_h 且无 parent → 不设上限（现行为）。
            // worker SLOT_TRUE_HEIGHT off 时 op 不带 slot_h → 全路径自动回退，byte-identical。
            if (typeof o.slot_h === 'number' && o.slot_h > 0) {
              h = Math.min(h, o.slot_h);
            } else if (o.parent) {
              const slot = objects.find(p => p.id === o.parent && p.type === 'frame');
              if (slot && slot.h > 0) h = Math.min(h, slot.h);
            }
            return { ...landed, h };
          });
        }
        if (action.job.status === 'error') {
          return objects.map(o =>
            o.job_id === action.job.id ? { ...o, pending: false, error: action.job.error ?? 'job failed' } : o
          );
        }
        return null;
      };
      let anyTouched = false;
      const projects = state.projects.map(p => {
        let touched = false;
        const pages = p.pages.map(pg => {
          const objs = patchObjects(pg.canvas.objects);
          if (!objs) return pg;
          touched = true;
          return { ...pg, canvas: { ...pg.canvas, objects: objs } };
        });
        if (touched) anyTouched = true;
        return touched ? { ...p, updatedAt: Date.now(), pages } : p;
      });
      // When polling touched no objects, keep the projects reference unchanged so the undo wrapper can detect "document unchanged" and skip the stack
      return { ...state, jobs, projects: anyTouched ? projects : state.projects };
    }
    case 'IMG_RELAYED': {
      // SPEC-415 T-703: relay swap with race guard — the object may live in any project/page (job results land
      // by job_id wherever the object is), so search globally; swap only if it still exists AND still points at
      // oldSrc (user may have deleted/replaced it mid-relay → no-op, the fresh upload becomes a GC orphan).
      let anyTouched = false;
      const projects = state.projects.map(p => {
        let touched = false;
        const pages = p.pages.map(pg => {
          if (!pg.canvas.objects.some(o => o.id === action.id && o.src === action.oldSrc)) return pg;
          touched = true;
          return {
            ...pg,
            canvas: {
              ...pg.canvas,
              objects: pg.canvas.objects.map(o =>
                o.id === action.id && o.src === action.oldSrc ? { ...o, src: action.newSrc } : o
              ),
            },
          };
        });
        if (touched) anyTouched = true;
        return touched ? { ...p, updatedAt: Date.now(), pages } : p;
      });
      return anyTouched ? { ...state, projects } : state;
    }
    case 'SELECT': {
      if (!action.id) return { ...state, selectedId: null, selectedIds: [] };
      if (action.additive) {
        const has = state.selectedIds.includes(action.id);
        const selectedIds = has ? state.selectedIds.filter(i => i !== action.id) : [...state.selectedIds, action.id];
        return { ...state, selectedIds, selectedId: selectedIds[selectedIds.length - 1] ?? null };
      }
      // Grouped objects: clicking any member selects the whole group (hidden members excluded — SPEC-405;
      // a hidden object itself is never group-expanded, it single-selects via the fallthrough below)
      const pg = currentPage(state);
      const obj = pg?.canvas.objects.find(o => o.id === action.id);
      if (obj?.group_id && pg && obj.visible !== false) {
        const ids = pg.canvas.objects.filter(o => o.group_id === obj.group_id && o.visible !== false).map(o => o.id);
        return { ...state, selectedIds: ids, selectedId: action.id };
      }
      return { ...state, selectedId: action.id, selectedIds: [action.id] };
    }
    case 'SELECT_MANY': {
      const cur = new Set(state.selectedIds);
      const ids = action.additive
        ? [...state.selectedIds, ...action.ids.filter(i => !cur.has(i))]
        : action.ids;
      return { ...state, selectedIds: ids, selectedId: ids[ids.length - 1] ?? null };
    }
    case 'GROUP': {
      if (state.selectedIds.length < 2) return state;
      const gid = `g${Date.now().toString(36)}`;
      const ids = new Set(state.selectedIds);
      return withCurrentPage(state, pg => ({
        ...pg,
        canvas: { ...pg.canvas, objects: pg.canvas.objects.map(o => ids.has(o.id) ? { ...o, group_id: gid } : o) },
      }));
    }
    case 'UNGROUP': {
      const ids = new Set(state.selectedIds);
      return withCurrentPage(state, pg => ({
        ...pg,
        canvas: { ...pg.canvas, objects: pg.canvas.objects.map(o => ids.has(o.id) ? { ...o, group_id: undefined } : o) },
      }));
    }
    case 'ALIGN_MANY': {
      // Multi-select alignment: align uniformly against the combined bounding box of the selection.
      if (state.selectedIds.length < 2) return state;
      const pg = currentPage(state);
      if (!pg) return state;
      const ids = new Set(state.selectedIds);
      const sel = pg.canvas.objects.filter(o => ids.has(o.id));
      if (sel.length < 2) return state;
      const minX = Math.min(...sel.map(o => o.x));
      const maxX = Math.max(...sel.map(o => o.x + o.w));
      const minY = Math.min(...sel.map(o => o.y));
      const maxY = Math.max(...sel.map(o => o.y + o.h));
      const dxOf = (o: CanvasObject): number => {
        switch (action.dir) {
          case 'left': return minX - o.x;
          case 'hcenter': return (minX + maxX) / 2 - (o.x + o.w / 2);
          case 'right': return (maxX - o.w) - o.x;
          default: return 0;
        }
      };
      const dyOf = (o: CanvasObject): number => {
        switch (action.dir) {
          case 'top': return minY - o.y;
          case 'vcenter': return (minY + maxY) / 2 - (o.y + o.h / 2);
          case 'bottom': return (maxY - o.h) - o.y;
          default: return 0;
        }
      };
      return withCurrentPage(state, p => ({
        ...p,
        canvas: {
          ...p.canvas,
          objects: p.canvas.objects.map(o => ids.has(o.id) ? { ...o, x: o.x + dxOf(o), y: o.y + dyOf(o) } : o),
        },
      }));
    }
    case 'MOVE_MANY': {
      // Dragging any selected object moves the whole selection by the same delta (delta from the dragged object's old/new position)
      const pg = currentPage(state);
      const dragged = pg?.canvas.objects.find(o => o.id === action.id);
      if (!pg || !dragged) return state;
      const dx = action.x - dragged.x;
      const dy = action.y - dragged.y;
      if (dx === 0 && dy === 0) return state;
      // SPEC-426 M2 提示2: explicit batch set wins; omitted → the current selection (old behavior)
      const ids = new Set(action.ids ?? state.selectedIds);
      return withCurrentPage(state, p => {
        let objects = p.canvas.objects.map(o => {
          if (!ids.has(o.id)) return o;
          const next = { ...o, x: o.x + dx, y: o.y + dy };
          // SPEC-425: the dragged child leaves its frame (detach); a dragged frame keeps its
          // children — they re-resolve against its new position (whole-group translation).
          if (o.id === action.id && next.parent !== undefined) next.parent = undefined;
          return next;
        });
        if (hasLayout(objects)) objects = resolveLayout(objects, WEB_LAYOUT_OPTS);
        return { ...p, canvas: { ...p.canvas, objects } };
      });
    }
    case 'REMOVE_MANY': {
      const ids = new Set(action.ids);
      if (ids.size === 0) return state;
      // SPEC-425: removing frames cascades to their descendants (nested frames included)
      const pgSel = currentPage(state);
      const doomed = new Set(ids);
      if (pgSel) for (const id of ids) for (const d of collectDescendants(pgSel.canvas.objects, id)) doomed.add(d);
      return {
        ...withCurrentPage(state, pg => {
          let objects = pg.canvas.objects.filter(o => !doomed.has(o.id));
          if (hasLayout(objects)) objects = resolveLayout(objects, WEB_LAYOUT_OPTS);
          return { ...pg, canvas: { ...pg.canvas, objects } };
        }),
        selectedId: null,
        selectedIds: [],
      };
    }
    case 'PATCH_OBJECT': {
      // SPEC-426 M2 提示2: batch form — `id` may be an array; one patch applies to every target
      // in a single action (one undo entry). Single-string dispatches behave exactly as before.
      const targets = new Set(typeof action.id === 'string' ? [action.id] : action.id);
      const patchPage = (pg: Page): Page => {
        let objects = pg.canvas.objects.map(o => {
          if (!targets.has(o.id)) return o;
          const next = { ...o, ...action.patch };
          // SPEC-425: positioning a parented child at absolute coords = detach from its frame;
          // patching a frame (drag/resize) re-resolves its children against the new geometry.
          if (next.parent !== undefined && (action.patch.x !== undefined || action.patch.y !== undefined)) next.parent = undefined;
          // SPEC-455 M1: manually resizing an engine-sized object = detach from sizing (same
          // spirit as move-detaches-parent); otherwise the next resolveLayout pass would
          // silently overwrite the user's w/h.
          if (next.sizing !== undefined && (action.patch.w !== undefined || action.patch.h !== undefined)) delete next.sizing;
          return next;
        });
        if (hasLayout(objects)) objects = resolveLayout(objects, WEB_LAYOUT_OPTS);
        return { ...pg, canvas: { ...pg.canvas, objects } };
      };
      // Scoped variant (SPEC-371): async flows (region edit) pass the originating project/page so the result lands
      // back on the sender even when the user navigated away mid-generation; other call sites stay current-page.
      if (action.projectId && action.pageId) return withProjectPage(state, action.projectId, action.pageId, patchPage);
      return withCurrentPage(state, patchPage);
    }
    case 'RENAME_OBJECT': {
      // SPEC-426 M2 C.2 (修复轮): layer-panel double-click rename — object-level counterpart to
      // PROJECT_RENAME/PAGE_RENAME: trim, empty ignored, undoable. Same-name renames are no-ops
      // (don't pollute the undo stack); `name` never affects layout → no resolveLayout pass.
      const name = action.name.trim();
      if (!name) return state;
      const pg = currentPage(state);
      const obj = pg?.canvas.objects.find(o => o.id === action.id);
      if (!pg || !obj || obj.name === name) return state;
      return withCurrentPage(state, p => ({
        ...p,
        canvas: { ...p.canvas, objects: p.canvas.objects.map(o => o.id === action.id ? { ...o, name } : o) },
      }));
    }
    case 'TOGGLE_VISIBLE': {
      // SPEC-405: per-layer show/hide. Hiding an object also drops it from the selection —
      // hidden objects are not hit-testable on canvas, so a lingering selection would ghost a transformer.
      const pg = currentPage(state);
      const obj = pg?.canvas.objects.find(o => o.id === action.id);
      if (!pg || !obj) return state;
      const hiding = obj.visible !== false;
      const next = withCurrentPage(state, p => ({
        ...p,
        canvas: { ...p.canvas, objects: p.canvas.objects.map(o => o.id === action.id ? { ...o, visible: !hiding } : o) },
      }));
      if (!hiding) return next;
      const selectedIds = state.selectedIds.filter(i => i !== action.id);
      const selectedId = selectedIds.length === 0 ? null
        : selectedIds.includes(state.selectedId ?? '') ? state.selectedId
        : selectedIds[selectedIds.length - 1];
      return { ...next, selectedIds, selectedId };
    }
    case 'REORDER':
      return withCurrentPage(state, pg => {
        const objects = [...pg.canvas.objects];
        const i = objects.findIndex(o => o.id === action.id);
        if (i < 0) return pg;
        const j = action.dir === 'up' ? i + 1 : i - 1;
        if (j < 0 || j >= objects.length) return pg;
        [objects[i], objects[j]] = [objects[j], objects[i]];
        return { ...pg, canvas: { ...pg.canvas, objects } };
      });
    case 'REORDER_TO':
      return withCurrentPage(state, pg => {
        const objects = [...pg.canvas.objects];
        const i = objects.findIndex(o => o.id === action.id);
        if (i < 0) return pg;
        const [moved] = objects.splice(i, 1);
        const j = Math.max(0, Math.min(objects.length, action.toIndex));
        objects.splice(j, 0, moved);
        return { ...pg, canvas: { ...pg.canvas, objects } };
      });
    case 'REMOVE': {
      // SPEC-425: removing a frame cascades to its descendants (nested frames included)
      const pgSel = currentPage(state);
      const doomed = new Set(pgSel ? [action.id, ...collectDescendants(pgSel.canvas.objects, action.id)] : [action.id]);
      return {
        ...withCurrentPage(state, pg => {
          let objects = pg.canvas.objects.filter(o => !doomed.has(o.id));
          if (hasLayout(objects)) objects = resolveLayout(objects, WEB_LAYOUT_OPTS);
          return { ...pg, canvas: { ...pg.canvas, objects } };
        }),
        selectedId: doomed.has(state.selectedId ?? '') ? null : state.selectedId,
        selectedIds: state.selectedIds.filter(i => !doomed.has(i)),
      };
    }
    case 'PROJECT_NEW': {
      const p = makeProject(action.name, action.artboard);
      // New project opens straight into its canvas (SPEC-404: the gallery is the only project picker now)
      return { ...state, projects: [p, ...state.projects], currentId: p.id, currentPageId: p.pages[0].id, selectedId: null, selectedIds: [], view: 'workbench' };
    }
    case 'PROJECT_THUMB': {
      // Thumbnail refresh only — never touches updatedAt (card ordering stays truthful) and not undoable
      const target = state.projects.find(p => p.id === action.id);
      if (!target || target.thumb === action.thumb) return state;
      return { ...state, projects: state.projects.map(p => p.id === action.id ? { ...p, thumb: action.thumb } : p) };
    }
    case 'SET_VIEW': {
      if (state.view === action.view) return state;
      return { ...state, view: action.view };
    }
    case 'PROJECT_SWITCH': {
      const target = state.projects.find(p => p.id === action.id);
      if (!target) return state;
      return { ...state, currentId: action.id, currentPageId: target.pages[0]?.id ?? null, selectedId: null, selectedIds: [] };
    }
    case 'PROJECT_DELETE': {
      const projects = state.projects.filter(p => p.id !== action.id);
      let currentId = state.currentId;
      let currentPageId = state.currentPageId;
      if (currentId === action.id) {
        // Switch to the most recently updated project; null (empty state) if none remain
        const next = [...projects].sort((a, b) => b.updatedAt - a.updatedAt)[0] ?? null;
        currentId = next?.id ?? null;
        currentPageId = next?.pages[0]?.id ?? null;
      }
      // SPEC-454 M3: the deletion must survive the server-side union merge — leave a tombstone
      // (LWW against the project copy: deletedAt ≥ updatedAt wins the delete; a later rebuild with
      // a newer updatedAt removes it again). Replaces any older tombstone for the same id.
      const deleted = dedupeTombstones([...state.deleted, { id: action.id, deletedAt: Date.now() }]);
      return { ...state, projects, deleted, currentId, currentPageId, selectedId: null, selectedIds: [] };
    }
    case 'CANVAS_RESIZE': {
      // Resize the canvas (preset/custom). Objects are kept; anything outside the new bounds is left for the user/later edits.
      return withCurrentPage(state, pg => ({
        ...pg, canvas: { ...pg.canvas, artboard: { ...pg.canvas.artboard, w: action.w, h: action.h } },
      }));
    }
    case 'CANVAS_BG': {
      return withCurrentPage(state, pg => ({
        ...pg, canvas: { ...pg.canvas, artboard: { ...pg.canvas.artboard, bg: action.color } },
      }));
    }
    case 'PROJECT_RENAME': {
      const name = action.name.trim();
      if (!name) return state;
      return {
        ...state,
        projects: state.projects.map(p => p.id === action.id ? { ...p, name } : p),
      };
    }
    case 'PAGE_NEW': {
      // A new page inherits the current page's artboard size and background
      const cur = currentPage(state);
      const page = freshPage(cur ? cur.canvas.artboard : undefined, action.name);
      const next = withCurrent(state, p => ({ pages: [...p.pages, page] }));
      return { ...next, currentPageId: page.id, selectedId: null, selectedIds: [] };
    }
    case 'PAGE_SWITCH': {
      const p = currentProject(state);
      if (!p || !p.pages.some(pg => pg.id === action.id)) return state;
      return { ...state, currentPageId: action.id, selectedId: null, selectedIds: [] };
    }
    case 'PAGE_RENAME': {
      const name = action.name.trim();
      if (!name) return state;
      return withCurrent(state, p => ({ pages: p.pages.map(pg => pg.id === action.id ? { ...pg, name } : pg) }));
    }
    case 'PAGE_DELETE': {
      const p = currentProject(state);
      if (!p || p.pages.length <= 1) return state; // keep at least one page
      let currentPageId = state.currentPageId;
      const next = withCurrent(state, proj => {
        const pages = proj.pages.filter(pg => pg.id !== action.id);
        return { pages };
      });
      if (currentPageId === action.id) {
        const np = currentProject(next);
        currentPageId = np?.pages[0]?.id ?? null;
      }
      return { ...next, currentPageId, selectedId: null, selectedIds: [] };
    }
    case 'COMPONENT_SAVE': {
      // Save the selected objects (≥1) as a component — name + snapshot, coordinates normalized to the bounding-box top-left.
      const pg = currentPage(state);
      if (!pg || state.selectedIds.length === 0) return state;
      const sel = new Set(state.selectedIds);
      const objs = pg.canvas.objects.filter(o => sel.has(o.id));
      if (objs.length === 0) return state;
      const minX = Math.min(...objs.map(o => o.x));
      const minY = Math.min(...objs.map(o => o.y));
      const maxX = Math.max(...objs.map(o => o.x + o.w));
      const maxY = Math.max(...objs.map(o => o.y + o.h));
      const comp: ComponentDef = {
        id: uid('c'),
        name: action.name.trim() || `${state.components.length + 1}`,
        created_at: Date.now(),
        w: maxX - minX,
        h: maxY - minY,
        // Strip runtime fields (job/error/in-page navigation links/visibility), keep only geometry + content
        // snapshot; group_id is kept and remapped on insert. visible is stripped so inserted components are always shown.
        objects: objs.map(o => {
          const clone: CanvasObject = { ...o, x: o.x - minX, y: o.y - minY };
          delete clone.job_id; delete clone.pending; delete clone.error; delete clone.link_to; delete clone.visible; delete clone._born;
          return clone;
        }),
      };
      return { ...state, components: [comp, ...state.components] };
    }
    case 'COMPONENT_DELETE':
      return { ...state, components: state.components.filter(c => c.id !== action.id) };
    case 'COMPONENT_INSERT': {
      // Clone with fresh ids and insert at the center of the current page's canvas; objects sharing a group in the source remap to one new gid.
      const comp = state.components.find(c => c.id === action.id);
      const pg = currentPage(state);
      if (!comp || !pg) return state;
      const ab = pg.canvas.artboard;
      const offX = (ab.w - comp.w) / 2;
      const offY = (ab.h - comp.h) / 2;
      const gidMap = new Map<string, string>();
      const clones: CanvasObject[] = comp.objects.map(o => {
        let group_id: string | undefined;
        if (o.group_id) {
          if (!gidMap.has(o.group_id)) gidMap.set(o.group_id, uid('g'));
          group_id = gidMap.get(o.group_id);
        }
        return { ...o, id: uid('o'), x: o.x + offX, y: o.y + offY, group_id };
      });
      const ids = clones.map(c => c.id);
      return {
        ...withCurrentPage(state, p => ({ ...p, canvas: { ...p.canvas, objects: [...p.canvas.objects, ...clones] } })),
        selectedIds: ids,
        selectedId: ids[ids.length - 1] ?? null,
      };
    }
    case 'LOAD':
      return action.state;
    default:
      return state;
  }
}

export function applyOps(canvas: CanvasState, ops: CanvasOp[]): CanvasState {
  // SPEC-426 M2 C.1: per-op semantics live in the shared applier (web/src/lib/canvasOps.ts) —
  // the worker mirror (agent.ts applyOpLocally) runs the exact same code, so mirror ≡ web by
  // construction. The web-only runtime stamp (_born, SPEC-409 ghost cursor) is applied here so
  // the shared module stays pure and mirror-safe.
  let out = canvas;
  for (const op of ops) {
    out = applyOpLocally(out, op, WEB_LAYOUT_OPTS);
    if (op.op === 'add_image' || op.op === 'add_icon' || op.op === 'add_text' || op.op === 'add_rect' || op.op === 'add_frame') { // SPEC-457 M1: +add_icon
      const i = out.objects.findIndex(o => o.id === op.id);
      if (i >= 0) {
        const objects = [...out.objects];
        objects[i] = { ...objects[i], _born: Date.now() };
        out = { ...out, objects };
      }
    }
  }
  return out;
}

// ===== Persistence =====

const STORAGE_KEY_V3 = 'design-v3';
const STORAGE_KEY_V2 = 'design-v2';
const STORAGE_KEY_V1 = 'design-mvp-state-v1';
const CHAT_KEEP = 20;

interface PersistedV3 {
  version: 3;
  currentId: string | null;
  currentPageId: string | null;
  projects: Project[];
  /** Component library lives at the doc top level, reusable across projects */
  components?: ComponentDef[];
  /** Cloud sync: save timestamp of this doc (used for cross-device last-write-wins comparison) */
  savedAt?: number;
  /** Home gallery vs canvas workbench; missing on old docs → home (SPEC-404) */
  view?: View;
  /** SPEC-454 M3: tombstones of deleted projects; missing on pre-M3 docs → treated as [] */
  deleted?: Tombstone[];
}

/** Build the doc to persist (shared by localStorage and the cloud). */
export function buildPersistedDoc(state: AppState): PersistedV3 {
  return {
    version: 3,
    currentId: state.currentId,
    currentPageId: state.currentPageId,
    projects: state.projects.map(p => ({ ...p, chat: p.chat.slice(-CHAT_KEEP) })),
    components: state.components,
    deleted: state.deleted,
    savedAt: Date.now(),
    view: state.view,
  };
}

/** Parse a doc from any source (localStorage / cloud). Invalid structure → null. */
export function parsePersistedDoc(raw: unknown): { projects: Project[]; currentId: string | null; currentPageId: string | null; components: ComponentDef[]; deleted: Tombstone[]; savedAt: number; view: View } | null {
  if (!raw || typeof raw !== 'object') return null;
  const doc = raw as PersistedV3;
  if (doc.version !== 3 || !Array.isArray(doc.projects)) return null;
  const currentId = doc.currentId && doc.projects.some(p => p.id === doc.currentId) ? doc.currentId : (doc.projects[0]?.id ?? null);
  const proj = doc.projects.find(p => p.id === currentId) ?? null;
  const currentPageId = proj
    ? (doc.currentPageId && proj.pages.some(pg => pg.id === doc.currentPageId) ? doc.currentPageId : (proj.pages[0]?.id ?? null))
    : null;
  return {
    projects: doc.projects,
    currentId,
    currentPageId,
    components: Array.isArray(doc.components) ? doc.components : [],
    // SPEC-454 M3: tolerant parse — pre-M3 docs carry no `deleted`; junk entries are dropped
    deleted: Array.isArray(doc.deleted)
      ? doc.deleted.filter((t): t is Tombstone => !!t && typeof t === 'object' && typeof (t as Tombstone).id === 'string' && typeof (t as Tombstone).deletedAt === 'number')
      : [],
    savedAt: typeof doc.savedAt === 'number' ? doc.savedAt : 0,
    // Tolerant parse: anything but the exact 'workbench' token falls back to home (old docs / bad data)
    view: doc.view === 'workbench' ? 'workbench' : 'home',
  };
}

export function persist(state: AppState): void {
  try {
    localStorage.setItem(STORAGE_KEY_V3, JSON.stringify(buildPersistedDoc(state)));
  } catch { /* over quota — just drop it; local storage only */ }
}

/** v2 single-canvas project → pages[] migration */
function migrateV2Project(p: { id: string; name: string; createdAt: number; updatedAt: number; canvas?: CanvasState; pages?: Page[]; chat?: ChatMsg[] }, pageName: string): Project {
  if (Array.isArray(p.pages) && p.pages.length > 0) {
    return { id: p.id, name: p.name, createdAt: p.createdAt, updatedAt: p.updatedAt, pages: p.pages, chat: p.chat ?? [] };
  }
  const now = Date.now();
  const page: Page = {
    id: `pg${p.id.slice(-4)}${now.toString(36).slice(-3)}`,
    name: pageName,
    canvas: p.canvas ?? freshCanvas(),
  };
  return { id: p.id, name: p.name, createdAt: p.createdAt, updatedAt: p.updatedAt, pages: [page], chat: p.chat ?? [] };
}

/** Boot-time restore. Returns null = brand-new user (the caller seeds a project). */
export function restore(): { projects: Project[]; currentId: string | null; currentPageId: string | null; components: ComponentDef[]; deleted: Tombstone[]; savedAt: number; view: View } | null {
  // v3 native
  try {
    const raw = localStorage.getItem(STORAGE_KEY_V3);
    if (raw) {
      const parsed = parsePersistedDoc(JSON.parse(raw));
      if (parsed) return parsed;
    }
  } catch { /* fall through to v2 */ }
  // v2 migration: single-canvas project → single-page project
  try {
    const raw = localStorage.getItem(STORAGE_KEY_V2);
    if (raw) {
      const doc = JSON.parse(raw) as { version?: number; currentId?: string | null; projects?: any[] };
      if (doc.version === 2 && Array.isArray(doc.projects)) {
        const projects = doc.projects.map(p => migrateV2Project(p, '1'));
        const currentId = doc.currentId && projects.some(p => p.id === doc.currentId) ? doc.currentId : (projects[0]?.id ?? null);
        const proj = projects.find(p => p.id === currentId) ?? null;
        return { projects, currentId, currentPageId: proj?.pages[0]?.id ?? null, components: [], deleted: [], savedAt: 0, view: 'home' as View };
      }
    }
  } catch { /* fall through to v1 */ }
  // v1 migration: single project → projects[0]
  try {
    const raw = localStorage.getItem(STORAGE_KEY_V1);
    if (!raw) return null;
    const old = JSON.parse(raw) as { canvas?: CanvasState; chat?: ChatMsg[] };
    if (!old.canvas) return null;
    const now = Date.now();
    const page: Page = { id: `pg${now.toString(36)}mig`, name: '1', canvas: old.canvas };
    const p: Project = {
      id: `p${now.toString(36)}mig`,
      name: 'Untitled design',
      createdAt: now,
      updatedAt: now,
      pages: [page],
      chat: Array.isArray(old.chat) ? old.chat : [],
    };
    return { projects: [p], currentId: p.id, currentPageId: page.id, components: [], deleted: [], savedAt: 0, view: 'home' as View };
  } catch { return null; }
}
